import {cn} from '@tempmee/core'
import {OutlineXIcon} from '@tempmee/react-icons'
import type {VariantProps} from 'class-variance-authority'
import {
  type HTMLAttributes,
  type ReactNode,
  createContext,
  forwardRef,
  useContext,
  useEffect,
  useId,
  useRef,
} from 'react'
import {createPortal} from 'react-dom'
import {IconButton} from '../icon-button'
import {Scrim} from '../scrim'
import {Typography} from '../typography'
import {DialogPanelVariants} from './Variants'

// ---------------------------------------------------------------------------
// Context — lets DialogTitle reach the dialog's `onClose` + labelling id
// ---------------------------------------------------------------------------

interface DialogContextValue {
  onClose?: () => void
  titleId: string
}

const DialogContext = createContext<DialogContextValue | null>(null)

// ---------------------------------------------------------------------------
// DialogTitle — header row: title (left) + optional close IconButton (right)
// ---------------------------------------------------------------------------

export interface DialogTitleProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Show a close button. Defaults to the dialog's `onClose` when omitted; pass
   * `false`/`null` to hide it, or a handler to override.
   */
  onClose?: (() => void) | false | null
  /** Label for the close button. @default 'Close' */
  closeLabel?: string
}

export const DialogTitle = forwardRef<HTMLDivElement, DialogTitleProps>(
  ({className, children, onClose, closeLabel = 'Close', ...props}, ref) => {
    const ctx = useContext(DialogContext)
    // `undefined` → inherit dialog onClose; explicit `false`/`null` → no button.
    const handleClose = onClose === undefined ? ctx?.onClose : onClose || undefined

    return (
      <div
        ref={ref}
        className={cn('flex items-center justify-between gap-2 p-4', className)}
        {...props}
      >
        {typeof children === 'string' ? (
          <Typography id={ctx?.titleId} variant="h6" className="whitespace-nowrap">
            {children}
          </Typography>
        ) : (
          children
        )}
        {handleClose && (
          <IconButton
            aria-label={closeLabel}
            appearance="default"
            color="secondary"
            Icon={<OutlineXIcon />}
            onClick={handleClose}
            className="shrink-0"
          />
        )}
      </div>
    )
  },
)
DialogTitle.displayName = 'DialogTitle'

// ---------------------------------------------------------------------------
// DialogContent — body region, optional top/bottom dividers
// ---------------------------------------------------------------------------

export interface DialogContentProps extends HTMLAttributes<HTMLDivElement> {
  /** Add top + bottom `divider` rules around the content (MUI's `dividers`). */
  dividers?: boolean
}

export const DialogContent = forwardRef<HTMLDivElement, DialogContentProps>(
  ({className, dividers = false, children, ...props}, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col px-4 text-text-primary',
        dividers ? 'my-1 border-y border-divider py-4' : 'pb-5',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
)
DialogContent.displayName = 'DialogContent'

// ---------------------------------------------------------------------------
// DialogActions — right-aligned button row
// ---------------------------------------------------------------------------

export interface DialogActionsProps extends HTMLAttributes<HTMLDivElement> {}

export const DialogActions = forwardRef<HTMLDivElement, DialogActionsProps>(
  ({className, children, ...props}, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center justify-end gap-2 px-4 pb-4', className)}
      {...props}
    >
      {children}
    </div>
  ),
)
DialogActions.displayName = 'DialogActions'

// ---------------------------------------------------------------------------
// Dialog — root (portal + backdrop + focus/scroll/escape handling)
// ---------------------------------------------------------------------------

export type DialogSize = 'small' | 'medium' | 'large'

export interface DialogProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>,
    VariantProps<typeof DialogPanelVariants> {
  /** Whether the dialog is visible. */
  open: boolean
  /** Called when the dialog requests to close (backdrop / Escape / close button). */
  onClose: () => void
  /** Panel width. @default 'medium' */
  size?: DialogSize
  /** Close when clicking the backdrop. @default true */
  closeOnOverlayClick?: boolean
  /** Close when pressing Escape. @default true */
  closeOnEscape?: boolean
  children?: ReactNode
}

export const Dialog = forwardRef<HTMLDivElement, DialogProps>(
  (
    {
      className,
      size,
      open,
      onClose,
      closeOnOverlayClick = true,
      closeOnEscape = true,
      children,
      ...props
    },
    ref,
  ) => {
    const titleId = useId()
    const internalRef = useRef<HTMLDivElement>(null)
    const previousActiveElementRef = useRef<Element | null>(null)

    // Escape to close
    useEffect(() => {
      if (!open || !closeOnEscape) return
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') onClose()
      }
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }, [open, closeOnEscape, onClose])

    // Lock body scroll while open
    useEffect(() => {
      if (!open) return
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = originalOverflow
      }
    }, [open])

    // Move focus into the panel; restore it on close
    useEffect(() => {
      if (!open) return
      previousActiveElementRef.current = document.activeElement
      const panel = (ref as React.RefObject<HTMLDivElement>)?.current ?? internalRef.current
      panel?.focus()
      return () => {
        if (previousActiveElementRef.current instanceof HTMLElement) {
          previousActiveElementRef.current.focus()
        }
      }
    }, [open, ref])

    if (!open) return null

    return createPortal(
      <DialogContext.Provider value={{onClose, titleId}}>
        {/* Modal tier (MUI scale: appBar 1100 < drawer 1200 < modal 1300 <
            snackbar 1400). `z-50` sat under app chrome (e.g. a sticky navbar),
            letting the header show through the scrim. */}
        <div className="fixed inset-0 z-[1300]">
          {/* Dim scrim; keyboard users close via Escape (handled above). `z-0`
              keeps it beneath the panel within this stacking context. */}
          <Scrim
            open
            onClick={closeOnOverlayClick ? onClose : undefined}
            data-testid="dialog-overlay"
            className="z-0"
          />
          <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
            <div
              ref={ref ?? internalRef}
              // biome-ignore lint/a11y/useSemanticElements: role="dialog" is the correct ARIA pattern for a modal panel; no native HTML element provides it
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              tabIndex={-1}
              className={cn(DialogPanelVariants({size}), className)}
              {...props}
            >
              {children}
            </div>
          </div>
        </div>
      </DialogContext.Provider>,
      document.body,
    )
  },
)
Dialog.displayName = 'Dialog'
